import React, { useEffect, useState } from 'react';
import { Linking } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchPlans,
  fetchUserSubscriptions,
  createSubscription,
} from 'src/redux/actions/subscriptionAction';
import { IStore } from 'src/redux/reducers';
import { ISubscriptionPlan } from 'src/models/Subscription';
import { getText } from 'src/utils/i18n';
import {
  Box,
  Button,
  Center,
  HStack,
  ScrollView,
  Spinner,
  Text,
  VStack,
  Heading,
  AlertDialog,
  Badge,
  Pressable,
} from 'native-base';

type BillingPeriod = 'monthly' | 'annual';

const SubscriptionPlans: React.FC<any> = ({ navigation }) => {
  const dispatch = useDispatch();
  const subscriptionState = useSelector((state: IStore) => state.subscription.createSubscription);

  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<ISubscriptionPlan | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedTab, setSelectedTab] = useState<BillingPeriod>('monthly');
  const cancelRef = React.useRef(null);

  const username = useSelector(
    (s: IStore) => s.user.userProfile.response?.username,
  );

  const { plans, userSubs } = useSelector((s: IStore) => s.subscription);


  useEffect(() => {
    if (username) {
      dispatch(fetchPlans.request({ username }));
      dispatch(fetchUserSubscriptions.request({ username }));
    }
  }, [dispatch, username]);

  useEffect(() => {
    if (subscriptionState.data) {
      console.log('Subscription created:', subscriptionState.data);
      setIsProcessing(false);
      setSelectedPlan(null);
      if (subscriptionState.data.short_url) {
        Linking.openURL(subscriptionState.data.short_url)
          .catch(err => console.error('Failed to open URL:', err));
      }
    }
  }, [subscriptionState.data]);

  useEffect(() => {
    if (subscriptionState.error) {
      console.error('Subscription creation failed:', subscriptionState.error);
      setIsProcessing(false);
    }
  }, [subscriptionState.error]);

  const handleConfirmSubscription = async () => {
    if (!selectedPlan || !username) return;

    setIsProcessing(true);
    setIsConfirmDialogOpen(false);
    console.log('Creating subscription for plan:', selectedPlan);

    const now = new Date();
    const months = selectedPlan.billingPeriod === 'annual' ? 12 : 1;
    const expiryDate = new Date(now.setMonth(now.getMonth() + months));

    const payload = {
      id: '', // Will be set by backend
      username,
      productType: 'GRAIN_ANALYSIS',
      numberOfMonths: months,
      planId: selectedPlan.id,
      planID: selectedPlan.id,
      status: 'created' as const,
      createRazorPaySubscription: true,
      expiry: Math.floor(expiryDate.getTime() / 1000),
      createdAt: Date.now(),
      paymentUrl: "",
    };

    dispatch(createSubscription.request({ username, payload }));
  };

  const handleCancelConfirmation = () => {
    setIsConfirmDialogOpen(false);
    setSelectedPlan(null);
  };

  // Helper function to check if user has active subscription for a specific plan
  const getUserSubscriptionForPlan = (planId: string) => {
    return userSubs.data.find(sub =>
      sub.status === 'SUBSCRIPTION_ACTIVE' &&
      (sub.planId === planId || sub.planID === planId)
    );
  };

  // Helper function to check if user has any active subscription
  const hasActiveSubscription = () => {
    return userSubs.data.some(sub => sub.status === 'SUBSCRIPTION_ACTIVE');
  };

  // Helper function to get button text and color scheme based on subscription status
  const getButtonProps = (plan: ISubscriptionPlan) => {
    const userSub = getUserSubscriptionForPlan(plan.id);
    const hasActive = hasActiveSubscription();

    if (userSub) {
      // User is already subscribed to this plan
      return {
        text: getText('cancel'),
        colorScheme: 'danger',
        variant: 'outline'
      };
    } else if (hasActive) {
      // User has other active subscription, show upgrade
      return {
        text: getText('upgrade'),
        colorScheme: 'warning',
        variant: 'solid'
      };
    } else {
      // User has no active subscription, show subscribe
      return {
        text: getText('subscribe'),
        colorScheme: 'primary',
        variant: 'solid'
      };
    }
  };

  // Handle different button actions based on subscription status
  const handlePlanAction = (plan: ISubscriptionPlan) => {
    const userSub = getUserSubscriptionForPlan(plan.id);

    if (userSub) {
      // Navigate to manage subscription to cancel
      navigation.navigate('ManageUserSubscription');
    } else {
      // Show subscription/upgrade confirmation
      setSelectedPlan(plan);
      setIsConfirmDialogOpen(true);
    }
  };

  // Get current plans based on selected tab
  const currentPlans = selectedTab === 'monthly' ? plans.data.monthly : plans.data.annual;

  return (
    <ScrollView flex={1} p={4}>
      {/* ─────────────── Available Plans ─────────────── */}
      <Heading mb={4}>{getText('subscriptionPlans')}</Heading>

      {/* Billing Period Tabs */}
      <HStack space={3} mb={6} justifyContent="center">
        <Pressable onPress={() => setSelectedTab('monthly')}>
          <Box
            bg={selectedTab === 'monthly' ? 'primary.600' : 'transparent'}
            borderWidth={1}
            borderColor={selectedTab === 'monthly' ? 'primary.600' : 'coolGray.300'}
            py={2}
            px={6}
            borderRadius="full"
            alignItems="center"
          >
            <Text
              color={selectedTab === 'monthly' ? 'white' : 'coolGray.700'}
              fontWeight={selectedTab === 'monthly' ? 'bold' : 'medium'}
              fontSize="sm"
            >
              {getText('monthly')}
            </Text>
          </Box>
        </Pressable>
        <Pressable onPress={() => setSelectedTab('annual')}>
          <Box
            bg={selectedTab === 'annual' ? 'primary.600' : 'transparent'}
            borderWidth={1}
            borderColor={selectedTab === 'annual' ? 'primary.600' : 'coolGray.300'}
            py={2}
            px={6}
            borderRadius="full"
            alignItems="center"
          >
            <Text
              color={selectedTab === 'annual' ? 'white' : 'coolGray.700'}
              fontWeight={selectedTab === 'annual' ? 'bold' : 'medium'}
              fontSize="sm"
            >
              {getText('annual')}
            </Text>
          </Box>
        </Pressable>
      </HStack>

      {plans.isFetching && (
        <Center py={4}>
          <Spinner size="lg" />
        </Center>
      )}
      {plans.error && (
        <Box bg="red.100" p={3} borderRadius="md" mb={4}>
          <Text color="red.700" fontWeight="medium">
            {plans.error.message}
          </Text>
        </Box>
      )}

      {subscriptionState.error && (
        <Box bg="red.100" p={3} borderRadius="md" mb={4}>
          <Text color="red.700" fontWeight="medium">
            Failed to create subscription: {subscriptionState.error.message}
          </Text>
        </Box>
      )}

      {isProcessing && (
        <Center py={4}>
          <Spinner size="lg" />
          <Text mt={2}>Processing your subscription...</Text>
        </Center>
      )}

      <HStack flexWrap="wrap" space={4} justifyContent="center" alignItems="stretch">
        {currentPlans.map(plan => {
          const buttonProps = getButtonProps(plan);
          const userSub = getUserSubscriptionForPlan(plan.id);
          const isPremium = plan.type === 'premium';

          return (
            <Box
              key={plan.id}
              borderWidth={isPremium ? 3 : 1}
              borderColor={userSub ? "success.400" : isPremium ? "amber.400" : "coolGray.300"}
              p={4}
              width={280}
              minHeight={400}
              borderRadius="lg"
              bg={userSub ? "success.50" : isPremium ? "amber.50" : "white"}
              shadow={isPremium ? 5 : 2}
            >
              <VStack space={3} flex={1} justifyContent="space-between">
                <VStack space={3} flex={1}>
                  {/* Header with badges */}
                  <VStack space={2}>
                    <HStack justifyContent="space-between" alignItems="flex-start">
                      <Heading size="md">{plan.name}</Heading>
                      {userSub && (
                        <Badge colorScheme="success" variant="solid" borderRadius="md">
                          ACTIVE
                        </Badge>
                      )}
                    </HStack>
                    <HStack space={2} flexWrap="wrap">
                      {isPremium && (
                        <Badge colorScheme="warning" variant="solid" borderRadius="md">
                          POPULAR
                        </Badge>
                      )}
                      {plan.discount && (
                        <Badge colorScheme="green" variant="solid" borderRadius="md">
                          {plan.discount}
                        </Badge>
                      )}
                    </HStack>
                  </VStack>

                  {/* Price */}
                  <Text fontSize="2xl" fontWeight="bold" color={isPremium ? "amber.600" : "primary.600"}>
                    {plan.displayPrice}
                  </Text>

                  {/* Features */}
                  <VStack space={2} flex={1}>
                    {plan.features.map((feature, index) => (
                      <HStack key={index} space={2} alignItems="flex-start">
                        <Text color="success.600" fontSize="md">✓</Text>
                        <Text flex={1} fontSize="sm">{feature}</Text>
                      </HStack>
                    ))}
                  </VStack>
                </VStack>

                {/* Subscribe Button - Always at bottom */}
                <Button
                  isDisabled={isProcessing}
                  onPress={() => handlePlanAction(plan)}
                  colorScheme={buttonProps.colorScheme}
                  variant={buttonProps.variant}
                  size="lg"
                >
                  {buttonProps.text}
                </Button>
              </VStack>
            </Box>
          );
        })}
      </HStack>

      {/* Confirmation Dialog */}
      <AlertDialog
        isOpen={isConfirmDialogOpen}
        onClose={handleCancelConfirmation}
        leastDestructiveRef={cancelRef}
      >
        <AlertDialog.Content>
          <AlertDialog.CloseButton />
          <AlertDialog.Header>
            {hasActiveSubscription() ? getText('confirmUpgrade') : getText('confirmSubscription')}
          </AlertDialog.Header>
          <AlertDialog.Body>
            {selectedPlan && (
              <VStack space={2}>
                <Text>
                  {hasActiveSubscription()
                    ? `Are you sure you want to upgrade to `
                    : `Are you sure you want to subscribe to `}
                  <Text bold>{selectedPlan.name}</Text>?
                </Text>
                <Text>
                  {getText('priceLabel')}: <Text bold>{selectedPlan.displayPrice}</Text>
                </Text>
                <Text>
                  {getText('billingLabel')}: <Text bold>{selectedPlan.billingPeriod === 'monthly' ? getText('monthly') : getText('annual')}</Text>
                </Text>
                {selectedPlan.discount && (
                  <Text color="green.600">
                    {getText('discountLabel')}: <Text bold>{selectedPlan.discount}</Text>
                  </Text>
                )}
                {hasActiveSubscription() && (
                  <Text fontSize="sm" color="warning.600" mt={2}>
                    {getText('subscriptionReplaceWarning')}
                  </Text>
                )}
                <Text fontSize="sm" color="gray.600" mt={2}>
                  {getText('redirectToPaymentInfo')}
                </Text>
              </VStack>
            )}
          </AlertDialog.Body>
          <AlertDialog.Footer>
            <Button.Group space={2}>
              <Button
                variant="unstyled"
                colorScheme="coolGray"
                onPress={handleCancelConfirmation}
                ref={cancelRef}
              >
                {getText('cancel')}
              </Button>
              <Button
                colorScheme="primary"
                onPress={handleConfirmSubscription}
              >
                Confirm
              </Button>
            </Button.Group>
          </AlertDialog.Footer>
        </AlertDialog.Content>
      </AlertDialog>
    </ScrollView>
  );
};

export default SubscriptionPlans;